import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Plus, ChevronDown, ChevronRight, Pencil, Trash2 } from 'lucide-react';
import api from '../../services/api';
import AddBreedingSeasonModal from './AddBreedingSeasonModal';
import BreedingSeasonContent from './BreedingSeasonContent';
import { useConfirm } from '../../components/common/ConfirmDialog';

export default function BreedingSeasonsCard({ horse, locations, catalogItems, onCoverAdded, onPregnancyChanged, isFarm }) {
  const [seasons, setSeasons]       = useState([]);
  const [loading, setLoading]       = useState(true);
  const [expanded, setExpanded]     = useState({});
  const [addModal, setAddModal]     = useState({ open: false, editing: null });

  const confirm = useConfirm();
  const fetchSeasons = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/breeding-seasons?horse_id=${horse.id}`);
      setSeasons(res.data || []);
    } catch { /* silently fail */ }
    setLoading(false);
  }, [horse.id]);

  useEffect(() => { fetchSeasons(); }, [fetchSeasons]);

  const toggleExpanded = async (id) => setExpanded(prev => ({ ...prev, [id]: !prev[id] }));

  const handleDelete = async (season) => {
    if (!await confirm({ title: `Delete ${season.year} breeding season?`, destructive: true })) return;
    await api.delete(`/breeding-seasons/${season.id}`);
    fetchSeasons();
  };

  return (
    <>
      <Card>
        <CardHeader className="flex flex-row items-center justify-between py-3">
          <CardTitle className="text-base">Breeding Seasons</CardTitle>
          <Button size="sm" variant="outline"
            onClick={() => setAddModal({ open: true, editing: null })}>
            <Plus className="w-4 h-4 mr-1" /> Add
          </Button>
        </CardHeader>
        <CardContent className="pt-0">
          {loading ? (
            <p className="text-sm text-center py-6 text-muted-foreground">Loading…</p>
          ) : seasons.length === 0 ? (
            <p className="text-sm text-center py-6 text-muted-foreground italic">
              No breeding seasons recorded. Click &quot;+ Add&quot; to create one.
            </p>
          ) : (
            <div className="divide-y">
              {seasons.map(season => {
                const isOpen = !!expanded[season.id];
                return (
                  <div key={season.id}>
                    {/* Season header row */}
                    <div className="flex items-center justify-between py-3 cursor-pointer hover:bg-muted/20 px-1 rounded"
                      onClick={() => toggleExpanded(season.id)}>
                      <div className="flex items-center gap-2 min-w-0">
                        {isOpen ? <ChevronDown className="w-4 h-4 shrink-0 text-muted-foreground" /> : <ChevronRight className="w-4 h-4 shrink-0 text-muted-foreground" />}
                        <span className="font-semibold text-sm">{season.year}</span>
                        {season.months?.length > 0 && (
                          <span className="text-xs text-muted-foreground">({season.months.join(', ')})</span>
                        )}
                        {season.covering_sires && (
                          <span className="text-sm text-muted-foreground truncate">· {season.covering_sires}</span>
                        )}
                      </div>
                      <div className="flex gap-1 shrink-0 ml-2" onClick={e => e.stopPropagation()}>
                        <Button variant="ghost" size="icon" className="h-7 w-7"
                          onClick={() => setAddModal({ open: true, editing: season })}>
                          <Pencil className="w-3.5 h-3.5" />
                        </Button>
                        <Button variant="ghost" size="icon" className="h-7 w-7"
                          onClick={() => handleDelete(season)}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    </div>
                    {/* Expanded content */}
                    {isOpen && (
                      <div className="pb-4 px-2">
                        <BreedingSeasonContent
                          season={season}
                          horse={horse}
                          locations={locations}
                          catalogItems={catalogItems}
                          onSeasonUpdated={fetchSeasons}
                          onCoverAdded={onCoverAdded}
                          onPregnancyChanged={onPregnancyChanged}
                          isFarm={isFarm}
                        />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      <AddBreedingSeasonModal
        open={addModal.open}
        onClose={() => setAddModal({ open: false, editing: null })}
        onSaved={() => { fetchSeasons(); setAddModal({ open: false, editing: null }); }}
        horse={horse}
        editing={addModal.editing}
      />
    </>
  );
}
